import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
// import { MobileView, DesktopView } from "react-device-detect";
import configServ from "../../services/config";
import MobileView from "../../Components/views/MobileView";
import DesktopView from "../../Components/views/DesktopView";
import ImagePreviewDialog from "../../Components/ImagePreviewDialog/ImagePreviewDialog";

const AttendanceStudentsDialog = ({
  open,
  handleClose,
  attendanceId,
  batchId,
}) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [searchType, setSearchType] = useState("registration_no");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchStudents = async () => {
    if (!attendanceId || !batchId) return;
    setLoading(true);
    try {
      const res = await configServ.fetchStudentsByAttendance({
        attendance_id: attendanceId,
        batch_id: batchId,
        searchType: searchType,
        searchQuery: searchQuery,
      });
      if (res.status) setStudents(res.students);
      else setStudents([]);
    } catch (err) {
      console.error(err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchStudents();
  }, [open, searchType, searchQuery]);

  const headers = [
    "Student Name",
    "Registration No",
    "Profile",
    "Remarks",
    "Attendence",

    // 👇 Add conditionally
  ];

  // ✅ Styles
  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#010911ff",
    color: "white",
    padding: 8,
    textAlign: "center",
  };

  const cellStyle = { py: 0.5, textAlign: "center" };
  const rowStyle = {
    "&:hover": { background: "#f5faff" },
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageBase64={selectedImage}
        title="Student Photo Preview"
      />
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center", // center horizontally
          alignItems: "center",
          position: "relative", // to position the close button absolutely
          color: "#1976d2", // MUI primary blue
          fontWeight: 600,
          fontSize: "1.25rem",
        }}
      >
        Att. List of Students
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#555",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Search & Filter Bar */}
      <Grid
        container
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mb: 2, pr: { xs: 1, sm: 2, md: 4 }, pl: { xs: 1, sm: 2, md: 4 } }}
      >
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Search Type</InputLabel>
            <Select
              value={searchType}
              label="Search Type"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value="registration_no">Registration No</MenuItem>
              <MenuItem value="full_name">Name</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={5} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Grid>
      </Grid>

      <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 5 }}>
            <CircularProgress />
          </Grid>
        ) : students.length === 0 ? (
          <Typography align="center" sx={{ py: 5 }}>
            No students found
          </Typography>
        ) : (
          <>
            <MobileView>
              <Grid container spacing={2}>
                {students.map((student) => (
                  <Grid item xs={12} key={student.id}>
                    <Card sx={{ display: "flex", alignItems: "center", p: 1 }}>
                      {student.student_image ? (
                        <Box
                          component="img"
                          src={student.student_image}
                          alt={student.full_name}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            cursor: "pointer",
                            objectFit: "cover",
                            border: "1px solid #ccc",
                          }}
                          onClick={() => {
                            setSelectedImage(student.student_image);
                            setPreviewOpen(true);
                          }}
                        />
                      ) : (
                        <CloseIcon
                          sx={{
                            width: 40,
                            height: 40,
                            color: "red",
                            borderRadius: "50%",
                            border: "1px solid #ccc",
                            padding: "8px",
                            backgroundColor: "#f9f9f9",
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="subtitle2">
                          {student.full_name}
                        </Typography>
                        <Typography variant="caption">
                          {student.registration_no}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{
                            color:
                              student.status === "present" ? "green" : "red", // both 'absent' and null will be red
                            fontWeight: 500,
                          }}
                        >
                          Status: {student.status ? student.status : "Absent"}
                        </Typography>

                        {student.student_remarks && (
                          <Typography variant="caption" display="block">
                            Remarks: {student.student_remarks}
                          </Typography>
                        )}

                        {student.photo_url ? (
                          <Typography variant="caption" display="block">
                            Profile:{" "}
                            <img
                              src={student.photo_url}
                              alt={student.full_name}
                              style={{
                                width: 50,
                                height: 50,
                                borderRadius: "50%",
                                marginRight: 8,
                              }}
                              onClick={() => {
                                setSelectedImage(student.photo_url);
                                setPreviewOpen(true);
                              }}
                            />
                          </Typography>
                        ) : (
                          <CloseIcon
                            sx={{
                              width: 40,
                              height: 40,
                              color: "red",
                              borderRadius: "50%",
                              border: "1px solid #ccc",
                              padding: "8px",
                              backgroundColor: "#f9f9f9",
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MobileView>

            <DesktopView>
              <TableContainer component={Paper}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell style={headerCellStyle}>Sr. No.</TableCell>
                      {headers.map((header, index) => (
                        <TableCell key={index} style={headerCellStyle}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {students.map((student, index) => (
                      <TableRow key={student.id} sx={rowStyle}>
                        <TableCell sx={cellStyle}>{index + 1}</TableCell>
                        <TableCell sx={cellStyle}>
                          {student.full_name}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {student.registration_no}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {student?.photo_url ? (
                            <Box
                              component="img"
                              src={student.photo_url}
                              alt={student.full_name}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                cursor: "pointer",
                                objectFit: "cover",
                                border: "1px solid #ccc",
                              }}
                              onClick={() => {
                                setSelectedImage(student.photo_url);
                                setPreviewOpen(true);
                              }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{
                                width: 40,
                                height: 40,
                                color: "red",
                                borderRadius: "50%",
                                border: "1px solid #ccc",
                                padding: "8px",
                                backgroundColor: "#f9f9f9",
                              }}
                            />
                          )}
                        </TableCell>
                        {/* <TableCell sx={cellStyle}>
                          {!student.status ? (
                            <Chip
                              label="Absent"
                              color="error"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Chip
                              label={student.status || "Not marked"}
                              color={
                                student.status === "present"
                                  ? "success"
                                  : "error"
                              }
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          )}
                        </TableCell> */}
                        <TableCell sx={cellStyle}>
                          {student.student_remarks || "-"}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* 📸 Camera Icon */}
                            {/* 🖼️ Student Image (if available) */}
                            {student.student_image ? (
                              <Box
                                component="img"
                                src={student.student_image}
                                alt={student.full_name}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  cursor: "pointer",
                                  objectFit: "cover",
                                  border: "1px solid #ccc",
                                }}
                                onClick={() => {
                                  setSelectedImage(student.student_image);
                                  setPreviewOpen(true);
                                }}
                              />
                            ) : (
                              <CloseIcon
                                sx={{
                                  width: 40,
                                  height: 40,
                                  color: "red",
                                  borderRadius: "50%",
                                  border: "1px solid #ccc",
                                  padding: "8px",
                                  backgroundColor: "#f9f9f9",
                                }}
                              />
                            )}

                            {/* ✅ Attendance Emoji */}
                            <Box
                              sx={{
                                fontSize: 20,
                                ml: 1,
                              }}
                            >
                              {student.status === "present" ? "✔️" : "❌"}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DesktopView>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceStudentsDialog;
